import { Injectable } from '@angular/core';
import { ApiService } from '@core/api/api.service';
import { BootstrapLoaderService } from '@core/loader/bootstrap-loader.service';
import { PermissionGroupDTO } from '@core/dto/permission-group.dto';
import { PermissionsDTO } from '@core/dto/permissions.dto';
import { Privileges, PrivilegeResource } from '@core/models/privileges.model';
import { AppStoreQuery, AppStoreService } from '@core/state';
import { UserNamespacedResourceBaseUrlBuilder } from '@core/user-namespaced-resource-base-url-builder.service';
import { deserialize } from '@junte/serialize-ts';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { AdditiveCapabilities, AdditiveOperation } from './additive-capabilities';

@Injectable({ providedIn: 'root' })
export class AccessControlService {
  constructor(
    private apiService: ApiService,
    private appStoreQuery: AppStoreQuery,
    private appStoreService: AppStoreService,
    private userNamespacedResourceBaseUrlBuilder: UserNamespacedResourceBaseUrlBuilder,
    private bootstrapLoader: BootstrapLoaderService,
  ) {}

  public getCapabilities(resource: PrivilegeResource): AdditiveCapabilities {
    return this.appStoreQuery.getValue()?.privileges.getCapabilities(resource) ?? new AdditiveCapabilities();
  }

  public getPrivileges(): Observable<Privileges> {
    // Guard: Don't make API call if no active organization (e.g., during logout)
    const baseUrl = this.userNamespacedResourceBaseUrlBuilder.buildBaseUrl();
    if (!baseUrl) {
      return of(new Privileges());
    }

    const url = `${baseUrl}/permission-groups/current`;
    return this.bootstrapLoader.track('privileges', this.apiService.get(url)).pipe(
      map((res: PermissionGroupDTO) => {
        const merged: PermissionsDTO = res.permissions;
        const privileges = deserialize(merged, Privileges);
        this.appStoreService.updatePrivileges(privileges);
        return privileges;
      }),
    );
  }

  public selectCanReadFrom(resource: PrivilegeResource): Observable<boolean> {
    return this.selectCanReadFromAll([resource]);
  }

  public selectCanReadFromAll(resources: PrivilegeResource[]): Observable<boolean> {
    return this.selectCanDo(resources, 'read', 'every');
  }

  public selectCanReadFromAny(resources: PrivilegeResource[]): Observable<boolean> {
    return this.selectCanDo(resources, 'read', 'some');
  }

  public selectCanManage(resource: PrivilegeResource): Observable<boolean> {
    return this.selectCanDo([resource], 'manage', 'every');
  }

  public selectCapabilities(resource: PrivilegeResource): Observable<AdditiveCapabilities> {
    return this.appStoreQuery.selectCapabilities(resource);
  }

  private selectCanDo(
    resources: PrivilegeResource[],
    operation: AdditiveOperation,
    quantifier: 'some' | 'every',
  ): Observable<boolean> {
    const observables = resources.map((item) =>
      this.selectCapabilities(item).pipe(map((capabilities) => (capabilities ? capabilities[operation] : false))),
    );

    return combineLatest(observables).pipe(map((privileges) => privileges[quantifier]((can) => can === true)));
  }
}
